/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import {
    object, shape, string,
} from 'prop-types';
import Grid from '@material-ui/core/Grid';
import ReactMarkdown from 'react-markdown';
import { useUIDSeed } from 'react-uid';
import { useQuery } from '@apollo/client';
import GraphqlSeoHeadTags from '../GraphqlCommonComponents/GraphqlSeoHeadTags/GraphqlSeoHeadTags';
import findContent from '../../../gql/queries/findContent';
import customBreakpoints from '../../../helpers/customBreakpoints';

const styles = (theme)  => ({
    container: {
        width: '100%',
        margin: 'auto',
        maxWidth: 1440,
        marginBottom: 50,
        marginTop: 25,
    },
    containerInner: {
        width: '100%',
        margin: 'auto',
        maxWidth: 980,
    },
    imageColumn: {
        textAlign: 'center',
        [theme.breakpoints.down(customBreakpoints.tabLandscape)]: {
            maxWidth: '100%',
            flexBasis: '100%',
        },
    },
    headingColumn: {
        [theme.breakpoints.down(customBreakpoints.tabLandscape)]: {
            maxWidth: '100%',
            flexBasis: '100%',
            marginTop: '20px',
        },
    },
    featureRow: {
        background: theme.palette.colorNeutral20,
        padding: '60px 40px 40px',
        margin: '15px 0',
    },
    heading: {
        fontSize: '24px',
        fontWeight: 'bold',
        display: 'flex',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
    },
    messageBody: {
        marginTop: 15,
    },
    markdownContent: {
        '& p': {
            lineHeight: 'normal',
            margin: '10px 0',
        },
        '& ul': {
            margin: '10px 0',
            paddingLeft: 40,
            lineHeight: 'normal',
        },
        '& li': {
            margin: '7px 0',
            lineHeight: 'normal',
        },
        '& a': {
            textDecoration: 'none',
            color: theme.palette.cms?.primary || theme.palette.colorPrimary,
        },
        '& img': {
            maxWidth: 135,
            marginTop: 10,
        },
    },
    contentRow: {
        lineHeight: 'normal',
        '& h2': {
            fontSize: 18,
            margin: '20px 0 10px',
        },
        '& h3': {
            fontSize: 16,
            margin: '20px 0 10px',
        },
    },
    banner: {
        '& img': {
            width: '100%',
        },
    },
});

const MessageFromCeoPage = ({ brand, classes }) => {
    const seed = useUIDSeed();
    const MESSAGE_FROM_CEO_QUERY = findContent({
        brand: brand?.domain || brand?.['domain-name'] || '1800flowers',
        contentType: 'message_from_ceo',
    });
    const { data, error, loading } = useQuery(MESSAGE_FROM_CEO_QUERY);

    if (loading || error) {
        return null;
    }

    const contentData = data?.findContent?.content?.entries?.[0];
    if (!contentData) {
        return null;
    }

    const {
        title = '',
        ceo_image: profileImage,
        hero_banner: heroBanner,
        messages = [],
        seo,
    } = contentData;

    const [primaryMessage, ...additionalMessages] = messages;

    return (
        <>
            <GraphqlSeoHeadTags seoData={seo} />
            <div className={classes.container}>
                {heroBanner?.url && (
                    <div className={classes.banner}>
                        <img src={heroBanner.url} alt={heroBanner.title || 'Banner'} />
                    </div>
                )}
                <div className={classes.containerInner}>
                    <div className={classes.featureRow}>
                        <Grid container>
                            <Grid className={classes.imageColumn} item xs={4}>
                                {profileImage?.url && <img src={profileImage.url} alt={profileImage.title || 'Profile'} />}
                            </Grid>
                            <Grid className={classes.headingColumn} item xs={8}>
                                <h1 className={classes.heading}> {title} </h1>
                            </Grid>

                            <Grid className={classes.messageBody} item xs={12}>
                                <ReactMarkdown className={classes.markdownContent} source={primaryMessage?.message} escapeHtml={false} />
                            </Grid>
                        </Grid>
                    </div>
                    <div className={classes.contentRow}>
                        <Grid container>
                            <Grid item xs={12}>
                                {additionalMessages.map((message) => (
                                    <ReactMarkdown
                                        key={seed(message)}
                                        className={classes.markdownContent}
                                        source={message.message}
                                        escapeHtml={false}
                                    />
                                ))}
                            </Grid>
                        </Grid>
                    </div>
                </div>
            </div>
        </>
    );
};

MessageFromCeoPage.propTypes = {
    brand: shape({
        domain: string,
        'domain-name': string,
    }),
    classes: object.isRequired,
};

MessageFromCeoPage.defaultProps = {
    brand: {},
};

export default withStyles(styles)(MessageFromCeoPage);
